import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useDeals, useCreateDeal, useUpdateDeal, useDeleteDeal, Deal } from '@/hooks/useDeals';
import { useAffiliatePartners } from '@/hooks/useAffiliatePartners';
import { Plus, Pencil, Trash2, Sparkles } from 'lucide-react';
import { format, isPast } from 'date-fns';
import { toast } from 'sonner';

interface DealFormData {
  affiliate_partner_id: string | null;
  title: string;
  description: string;
  discount_code: string;
  discount_percentage: number | null;
  discount_amount: number | null;
  currency: string;
  start_date: string;
  expiry_date: string;
  deal_url: string;
  is_active: boolean;
  is_featured: boolean;
}

const initialFormData: DealFormData = {
  affiliate_partner_id: null,
  title: '',
  description: '',
  discount_code: '',
  discount_percentage: null,
  discount_amount: null,
  currency: 'USD',
  start_date: new Date().toISOString().split('T')[0],
  expiry_date: '',
  deal_url: '',
  is_active: true,
  is_featured: false,
};

export default function DealsManagement() {
  const { data: deals, isLoading } = useDeals();
  const { data: partners } = useAffiliatePartners();
  const createDeal = useCreateDeal();
  const updateDeal = useUpdateDeal();
  const deleteDeal = useDeleteDeal();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [formData, setFormData] = useState<DealFormData>(initialFormData);

  const handleOpenCreate = () => {
    setEditingDeal(null);
    setFormData(initialFormData);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (deal: Deal) => {
    setEditingDeal(deal);
    setFormData({
      affiliate_partner_id: deal.affiliate_partner_id,
      title: deal.title,
      description: deal.description || '',
      discount_code: deal.discount_code || '',
      discount_percentage: deal.discount_percentage,
      discount_amount: deal.discount_amount,
      currency: deal.currency,
      start_date: deal.start_date?.split('T')[0] || '',
      expiry_date: deal.expiry_date?.split('T')[0] || '',
      deal_url: deal.deal_url || '',
      is_active: deal.is_active,
      is_featured: deal.is_featured,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      affiliate_partner_id: formData.affiliate_partner_id || null,
      discount_percentage: formData.discount_percentage || null,
      discount_amount: formData.discount_amount || null,
      expiry_date: formData.expiry_date || null,
      start_date: formData.start_date || new Date().toISOString(),
    };

    if (editingDeal) {
      await updateDeal.mutateAsync({ id: editingDeal.id, ...payload });
    } else {
      await createDeal.mutateAsync(payload);
    }
    
    setIsDialogOpen(false);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this deal?')) {
      await deleteDeal.mutateAsync(id);
    }
  };

  return (
    <>
      <Helmet>
        <title>Deals Management | Dashboard</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Sparkles className="w-8 h-8" />
              Deals Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage promotional deals and discounts
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleOpenCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Add Deal
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingDeal ? 'Edit Deal' : 'Create New Deal'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Partner (optional)</Label>
                  <Select
                    value={formData.affiliate_partner_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, affiliate_partner_id: v === 'none' ? null : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select partner..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Partner (Manual Entry)</SelectItem>
                      {partners?.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.partner_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., 20% Off All Flights"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Deal details..."
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Discount Code</Label>
                    <Input
                      value={formData.discount_code}
                      onChange={(e) => setFormData({ ...formData, discount_code: e.target.value })}
                      placeholder="e.g., SAVE20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Deal URL</Label>
                    <Input
                      value={formData.deal_url}
                      onChange={(e) => setFormData({ ...formData, deal_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Discount %</Label>
                    <Input
                      type="number"
                      value={formData.discount_percentage || ''}
                      onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value ? Number(e.target.value) : null })}
                      placeholder="e.g., 20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Discount Amount</Label>
                    <Input
                      type="number"
                      value={formData.discount_amount || ''}
                      onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value ? Number(e.target.value) : null })}
                      placeholder="e.g., 50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(v) => setFormData({ ...formData, currency: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expiry Date</Label>
                    <Input
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                    />
                    <Label>Active</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_featured}
                      onCheckedChange={(v) => setFormData({ ...formData, is_featured: v })}
                    />
                    <Label>Featured</Label>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createDeal.isPending || updateDeal.isPending}>
                    {editingDeal ? 'Update' : 'Create'} Deal
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : deals && deals.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Deal</TableHead>
                    <TableHead>Partner</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Expiry</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deals.map((deal) => {
                    const isExpired = deal.expiry_date && isPast(new Date(deal.expiry_date));
                    return (
                      <TableRow key={deal.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{deal.title}</p>
                            {deal.discount_code && (
                              <code className="text-xs bg-muted px-1 rounded">{deal.discount_code}</code>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{deal.partner?.partner_name || '—'}</TableCell>
                        <TableCell>
                          {deal.discount_percentage && `${deal.discount_percentage}%`}
                          {deal.discount_amount && `${deal.currency} ${deal.discount_amount}`}
                          {!deal.discount_percentage && !deal.discount_amount && '—'}
                        </TableCell>
                        <TableCell>
                          {deal.expiry_date ? format(new Date(deal.expiry_date), 'MMM d, yyyy') : 'No expiry'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {deal.is_featured && <Badge>Featured</Badge>}
                            {deal.is_active && !isExpired ? (
                              <Badge variant="outline" className="text-green-600 border-green-600">Active</Badge>
                            ) : (
                              <Badge variant="secondary">{isExpired ? 'Expired' : 'Inactive'}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="icon" variant="ghost" onClick={() => handleOpenEdit(deal)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDelete(deal.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="p-12 text-center">
                <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold">No Deals Yet</h3>
                <p className="text-muted-foreground mb-4">Create your first promotional deal</p>
                <Button onClick={handleOpenCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Deal
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
